import React from 'react';
import { Activity, AlertTriangle, CheckCircle2, Clock, Target, Users, Zap } from 'lucide-react';
interface SprintHealthScoreProps {
  score: number; // 0-100
  metrics: {
    completion: number;
    completedPts: number;
    totalPts: number;
    blockers: number;
    unestimated: number;
    daysRemaining: number;
    totalDays: number;
    doneCount: number;
    inProgressCount: number;
    totalIssues: number;
  };
  isLoading?: boolean;
}
export function SprintHealthScore({
  score,
  metrics,
  isLoading
}: SprintHealthScoreProps) {
  if (isLoading) {
    return <div className="animate-pulse space-y-4" data-id="element-2781">
        <div className="flex items-center gap-4" data-id="element-2782">
          <div className="w-24 h-24 rounded-full border-8 border-slate-800 shrink-0" data-id="element-2783"></div>
          <div className="flex-1 space-y-2" data-id="element-2784">
            <div className="h-4 bg-slate-800 rounded w-20" data-id="element-2785"></div>
            <div className="h-3 bg-slate-800 rounded w-32" data-id="element-2786"></div>
            <div className="h-2 bg-slate-800 rounded w-full mt-3" data-id="element-2787"></div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2" data-id="element-2788">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-12 bg-slate-800 rounded-lg" data-id="element-2789"></div>)}
        </div>
      </div>;
  }
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - score / 100 * circumference;
  // Color based on score
  let colorClass = 'text-emerald-400';
  let strokeColor = '#34d399';
  let glowColor = 'rgba(52, 211, 153, 0.15)';
  let statusText = 'Saludable';
  let StatusIcon = CheckCircle2;
  if (score < 40) {
    colorClass = 'text-red-400';
    strokeColor = '#f87171';
    glowColor = 'rgba(248, 113, 113, 0.15)';
    statusText = 'En Riesgo';
    StatusIcon = AlertTriangle;
  } else if (score < 70) {
    colorClass = 'text-amber-400';
    strokeColor = '#fbbf24';
    glowColor = 'rgba(251, 191, 36, 0.15)';
    statusText = 'Atención';
    StatusIcon = Activity;
  }
  // Sprint timeline progress
  const timeProgress = metrics.totalDays > 0 ? Math.min(100, Math.round((metrics.totalDays - metrics.daysRemaining) / metrics.totalDays * 100)) : 0;
  return <div className="flex flex-col h-full" data-id="element-2790">
      {/* Top section: Gauge + Status */}
      <div className="flex items-center gap-5" data-id="element-2791">
        {/* Compact Gauge */}
        <div className="relative w-24 h-24 shrink-0" data-id="element-2792">
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100" data-id="element-2793">
            <circle cx="50" cy="50" r={radius} className="stroke-slate-800" strokeWidth="8" fill="none" data-id="element-2794" />

            <circle cx="50" cy="50" r={radius} stroke={strokeColor} className="transition-all duration-1000 ease-out" strokeWidth="8" fill="none" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} style={{
            filter: `drop-shadow(0 0 6px ${glowColor})`
          }} data-id="element-2795" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-2796">
            <span className={`text-2xl font-bold ${colorClass}`} data-id="element-2797">{score}</span>
          </div>
        </div>

        {/* Status + Sprint Progress */}
        <div className="flex-1 min-w-0" data-id="element-2798">
          <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-bold ${colorClass}`} style={{
          backgroundColor: glowColor
        }} data-id="element-2799">
            <StatusIcon size={12} data-id="element-2800" />
            {statusText}
          </div>

          <div className="mt-2.5 flex items-center gap-2" data-id="element-2801">
            <span className="text-[10px] text-slate-500 shrink-0" data-id="element-2802">Sprint</span>
            <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden" data-id="element-2803">
              <div className="h-full bg-indigo-500 rounded-full transition-all duration-500" style={{
              width: `${metrics.completion}%`
            }} data-id="element-2804" />
            </div>
            <span className="text-[11px] font-semibold text-slate-300 shrink-0" data-id="element-2805">
              {metrics.completion}%
            </span>
          </div>

          <div className="mt-1.5 flex items-center gap-2" data-id="element-2806">
            <span className="text-[10px] text-slate-500 shrink-0" data-id="element-2807">Tiempo</span>
            <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden" data-id="element-2808">
              <div className={`h-full rounded-full transition-all duration-500 ${timeProgress > metrics.completion + 20 ? 'bg-amber-500' : 'bg-slate-600'}`} style={{
              width: `${timeProgress}%`
            }} data-id="element-2809" />
            </div>
            <span className="text-[11px] font-semibold text-slate-400 shrink-0" data-id="element-2810">
              {metrics.daysRemaining}d
            </span>
          </div>

          <p className="text-[10px] text-slate-600 mt-1.5" data-id="element-2811">
            {metrics.completedPts}/{metrics.totalPts} pts completados
          </p>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-4 gap-2 mt-4 pt-4 border-t border-slate-800/50" data-id="element-2812">
        <MetricCell icon={<CheckCircle2 size={11} className="text-emerald-400" data-id="element-2814" />} value={metrics.doneCount} label="Done" total={metrics.totalIssues} color="text-emerald-400" data-id="element-2813" />

        <MetricCell icon={<Zap size={11} className="text-blue-400" data-id="element-2816" />} value={metrics.inProgressCount} label="WIP" color="text-blue-400" data-id="element-2815" />

        <MetricCell icon={<AlertTriangle size={11} className="text-red-400" data-id="element-2818" />} value={metrics.blockers} label="Blockers" color={metrics.blockers > 0 ? 'text-red-400' : 'text-slate-400'} alert={metrics.blockers > 0} data-id="element-2817" />

        <MetricCell icon={<Target size={11} className="text-amber-400" data-id="element-2820" />} value={metrics.unestimated} label="Sin est." color={metrics.unestimated > 0 ? 'text-amber-400' : 'text-slate-400'} alert={metrics.unestimated > 2} data-id="element-2819" />
      </div>
    </div>;
}
function MetricCell({
  icon,
  value,
  label,
  total,
  color,
  alert
}: {
  icon: React.ReactNode;
  value: number;
  label: string;
  total?: number;
  color: string;
  alert?: boolean;
}) {
  return <div className={`rounded-lg p-2 text-center ${alert ? 'bg-slate-800/80' : 'bg-slate-800/30'}`} data-id="element-2821">
      <div className="flex items-center justify-center gap-1 mb-1" data-id="element-2822">{icon}</div>
      <p className={`text-base font-bold ${color}`} data-id="element-2823">
        {value}
        {total !== undefined && <span className="text-[10px] text-slate-600 font-normal" data-id="element-2824">
            /{total}
          </span>}
      </p>
      <p className="text-[9px] text-slate-500 leading-tight" data-id="element-2825">{label}</p>
    </div>;
}